import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { seasonEventAPI } from '../services/api';
import './Calendar.css';

const EVENT_TYPE_COLORS = {
  'Weekday Event': { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
  'Weekend Event': { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
  'Major Tournament': { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  'SCGA Team Event': { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  'Board Meeting': { bg: '#fce4ec', border: '#e91e63', text: '#c2185b' },
  'Course Maintenance': { bg: '#fff8e1', border: '#ffc107', text: '#ff8f00' },
  'Other': { bg: '#f5f5f5', border: '#9e9e9e', text: '#616161' }
};

const STATUS_COLORS = {
  'scheduled': { bg: '#e3f2fd', text: '#1976d2' },
  'registration-open': { bg: '#e8f5e9', text: '#388e3c' },
  'registration-closed': { bg: '#fff3e0', text: '#f57c00' },
  'completed': { bg: '#f5f5f5', text: '#757575' },
  'cancelled': { bg: '#ffebee', text: '#c62828' }
};

const Calendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(2026);
  const [selectedType, setSelectedType] = useState('all');
  const [expandedMonth, setExpandedMonth] = useState(null);

  const eventTypes = ['Weekday Event', 'Weekend Event', 'Major Tournament', 'SCGA Team Event', 'Board Meeting', 'Course Maintenance', 'Other'];

  useEffect(() => {
    fetchEvents();
  }, [selectedSeason]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await seasonEventAPI.getAll({ season: selectedSeason });
      setEvents(response.data.events || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const groupEventsByMonth = () => {
    const filtered = selectedType === 'all'
      ? events
      : events.filter(e => e.eventType === selectedType);

    const grouped = {};
    filtered.forEach(event => {
      const date = new Date(event.eventDate);
      // Use UTC methods to avoid timezone shifts when grouping by month
      const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

      if (!grouped[monthKey]) {
        grouped[monthKey] = { name: monthName, events: [] };
      }
      grouped[monthKey].events.push(event);
    });

    // Sort events within each month
    Object.values(grouped).forEach(month => {
      month.events.sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
    });

    return grouped;
  };

  // Format date for display - treats dates as UTC to avoid timezone shifts
  // This prevents "2026-01-06T00:00:00Z" from showing as "Jan 5" in Pacific time
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'  // Always interpret stored dates as UTC
    });
  };

  const handleCreateEvent = () => {
    navigate(`/calendar/events/new?season=${selectedSeason}`);
  };

  const handleViewEvent = (event) => {
    navigate(`/calendar/events/${event._id}`);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await seasonEventAPI.delete(eventId);
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event');
    }
  };

  const handleCreateTournament = (event) => {
    // Navigate to tournament setup with pre-filled data from the event
    navigate('/setup', {
      state: {
        fromEvent: {
          seasonEventId: event._id,
          name: event.name,
          date: event.eventDate,
          startTime: event.startTime,
          expectedPlayers: event.expectedPlayers
        }
      }
    });
  };

  const getTypeStats = () => {
    const stats = {};
    eventTypes.forEach(type => {
      stats[type] = events.filter(e => e.eventType === type).length;
    });
    return stats;
  };

  const groupedEvents = groupEventsByMonth();
  const typeStats = getTypeStats();

  if (loading) {
    return (
      <div className="calendar-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
          <h1>Season Calendar</h1>
        </div>
        <div className="header-right">
          <button className="btn-primary" onClick={handleCreateEvent}>
            + Add Event
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="calendar-controls">
        <div className="control-group">
          <label>Season:</label>
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>Event Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            {eventTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="calendar-stats">
        <div className="stats-summary">
          <span className="total-events">{events.length} Events</span>
          {eventTypes.map(type => typeStats[type] > 0 && (
            <span
              key={type}
              className="type-badge"
              style={{
                backgroundColor: EVENT_TYPE_COLORS[type].bg,
                color: EVENT_TYPE_COLORS[type].text,
                borderColor: EVENT_TYPE_COLORS[type].border
              }}
            >
              {type}: {typeStats[type]}
            </span>
          ))}
        </div>
      </div>

      <div className="calendar-grid">
        {Object.entries(groupedEvents).length === 0 ? (
          <div className="no-events">
            <p>No events found for {selectedSeason}</p>
            <button className="btn-primary" onClick={handleCreateEvent}>
              Create First Event
            </button>
          </div>
        ) : (
          Object.entries(groupedEvents)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([monthKey, monthData]) => (
              <div key={monthKey} className="month-card">
                <div
                  className="month-header"
                  onClick={() => setExpandedMonth(expandedMonth === monthKey ? null : monthKey)}
                >
                  <h2>{monthData.name}</h2>
                  <span className="event-count">{monthData.events.length} events</span>
                  <span className="expand-icon">{expandedMonth === monthKey ? '▼' : '▶'}</span>
                </div>

                <div className={`month-events ${expandedMonth === monthKey || expandedMonth === null ? 'expanded' : 'collapsed'}`}>
                  {monthData.events.map(event => (
                    <div
                      key={event._id}
                      className="event-card"
                      style={{
                        borderLeftColor: EVENT_TYPE_COLORS[event.eventType || 'Other'].border,
                        backgroundColor: EVENT_TYPE_COLORS[event.eventType || 'Other'].bg,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewEvent(event)}
                    >
                      <div className="event-main">
                        <div className="event-date">{formatDate(event.eventDate)}</div>
                        <div className="event-details">
                          <div className="event-name">{event.name}</div>
                          <div className="event-meta">
                            <span className="event-time">{event.startTime}</span>
                            <span className="event-players">{event.expectedPlayers} players</span>
                            <span
                              className="event-type-badge"
                              style={{
                                color: EVENT_TYPE_COLORS[event.eventType || 'Other'].text
                              }}
                            >
                              {event.eventType || 'Other'}
                            </span>
                            <span
                              className="event-status-badge"
                              style={STATUS_COLORS[event.status] || STATUS_COLORS.scheduled}
                            >
                              {event.status}
                            </span>
                          </div>
                          {event.description && (
                            <div className="event-description">{event.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                        {!event.linkedTournamentId && (
                          <button
                            className="btn-action btn-create-tournament"
                            onClick={() => handleCreateTournament(event)}
                            title="Create Tournament"
                          >
                            📋 Create Tournament
                          </button>
                        )}
                        {event.linkedTournamentId && (
                          <button
                            className="btn-action btn-view-tournament"
                            onClick={() => navigate(`/editor/${event.linkedTournamentId}`)}
                            title="View Tournament"
                          >
                            👁 View Tournament
                          </button>
                        )}
                        <button
                          className="btn-action btn-view"
                          onClick={() => handleViewEvent(event)}
                          title="View Details"
                        >
                          👁
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteEvent(event._id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
        )}
      </div>

    </div>
  );
};

export default Calendar;
